import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, DollarSign, Flag, Sparkles, ShieldCheck } from "lucide-react";
import Marquee from "@/components/Marquee";
import DTFMenu from "@/components/DTFMenu";
import SCMenu from "@/components/SCMenu";
import EtsyRow from "@/components/EtsyRow";
import CustomerGallery from "@/components/CustomerGallery";

const promises = [
  {
    icon: Zap,
    title: "Quick Turnaround",
    body: "Rushed orders are our specialty! We move fast without cutting corners.",
    tag: "SPEC / 01",
  },
  {
    icon: DollarSign,
    title: "Affordable Pricing",
    body: "Fair rates for premium prints and high quality products. No surprise fees either.",
    tag: "SPEC / 02",
  },
  {
    icon: Flag,
    title: "American Made",
    body: "Blanks, vinyl, ink, and labor — all sourced and produced in the USA.",
    tag: "SPEC / 03",
  },
  {
    icon: Sparkles,
    title: "Top-Notch Service",
    body: "Real humans & real communication. From proof to porch, we've got you.",
    tag: "SPEC / 04",
  },
];

const testimonials = [
  {
    body: "High quality t-shirts & hoodies. They aren't those cheap t-shirts that you get on Amazon or Temu... they are actually really comfortable and soft. Highly recommend.",
    author: "Chris M",
    role: "CA",
    verified: true,
    photo: null,
  },
  {
    body: "Turned around 200 stickers in ONE day! I love how the colors popped and the vibrancy was visible for a full year after!",
    author: "David S.",
    role: "KY",
    verified: true,
    photo: null,
  },
  {
    body: "Ordered flyers for our shop opening event. Great quality, super clean, and they really know how to pay attention to detail! 10/10.",
    author: "Rich R.",
    role: "",
    verified: true,
    photo: null,
  },
];

const TESTIMONIAL_ROTATE_MS = 7000; // 7s per slide, auto-rotates only when pool > 3

function TestimonialCard({ t, testid }) {
  return (
    <div className="brutal-card p-6 hard-shadow-red" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex gap-1 text-[color:var(--primary)]">
          {"★★★★★".split("").map((s, j) => (
            <span key={j} className="text-lg">{s}</span>
          ))}
        </div>
        {t.verified && (
          <span
            className="label-mono inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 border border-black"
            style={{ background: "var(--dtf-yellow-soft, #fff4b8)", color: "var(--dtf-blue)" }}
            title="Verified customer"
          >
            <ShieldCheck size={10} /> VERIFIED
          </span>
        )}
      </div>
      <p className="font-body text-lg mt-4 leading-snug">&ldquo;{t.body}&rdquo;</p>
      <div className="divider-thick mt-6 pt-3 flex justify-between items-center gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {t.photo && (
            <img
              src={t.photo}
              alt={t.author}
              className="w-10 h-10 rounded-full object-cover border-2 border-black shrink-0"
            />
          )}
          <span className="font-mono text-sm truncate">{t.author}</span>
        </div>
        {t.role && (
          <span className="label-mono text-[color:var(--muted-text)] shrink-0">{t.role}</span>
        )}
      </div>
    </div>
  );
}

function TestimonialsSection() {
  const [offset, setOffset] = useState(0);
  const [paused, setPaused] = useState(false);
  const canRotate = testimonials.length > 3;
  const visible = useMemo(() => {
    if (!canRotate) return testimonials;
    return [0, 1, 2].map((i) => testimonials[(offset + i) % testimonials.length]);
  }, [offset, canRotate]);

  useEffect(() => {
    if (!canRotate || paused) return;
    const id = setInterval(() => {
      setOffset((o) => (o + 1) % testimonials.length);
    }, TESTIMONIAL_ROTATE_MS);
    return () => clearInterval(id);
  }, [canRotate, paused]);

  return (
    <section className="section-pad" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="label-mono text-[color:var(--muted-text)]">CLIENT FEEDBACK</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] mt-3 max-w-3xl">
              THEY SAID IT<br />BETTER THAN WE COULD.
            </h2>
          </div>
          {canRotate && (
            <div className="flex items-center gap-2">
              <span className="label-mono text-[color:var(--muted-text)]">
                {offset + 1}–{Math.min(offset + 3, testimonials.length)} / {testimonials.length}
              </span>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="label-mono border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
                data-testid="testimonials-pause"
              >
                {paused ? "Play" : "Pause"}
              </button>
            </div>
          )}
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14"
          onMouseEnter={() => canRotate && setPaused(true)}
          onMouseLeave={() => canRotate && setPaused(false)}
        >
          {visible.map((t, i) => (
            <TestimonialCard
              key={`${offset}-${i}-${t.author}`}
              t={t}
              testid={`testimonial-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-7 px-6 md:px-12 pt-16 pb-12 md:pt-24 md:pb-20 fade-up">
            <p className="label-mono text-[color:var(--muted-text)]">AMERICAN OWNED &amp; OPERATED</p>
            <p className="font-body text-sm md:text-base text-[color:var(--muted-text)] mt-2 max-w-xl">
              Designing, printing, weeding, and pressing — all by our hardworking team of professionals in the industry!
            </p>
            <h1 className="font-display text-[13vw] md:text-[8rem] leading-[0.95] tracking-tighter mt-4">
              PRINTED<br />
              <span className="text-[color:var(--primary)]">LOUD.</span><br />
              SHIPPED<br />
              QUICK.
            </h1>
            <p className="font-body text-lg md:text-xl text-[color:var(--muted-text)] mt-8 max-w-xl">
              Custom apparel, weatherproof decals, and marketing prints.<br />
              Fair priced &amp; delivered before the deadline. That&rsquo;s the whole thing.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" className="btn-brutal btn-brutal-red" data-testid="hero-shop-btn">
                Shop The Line <ArrowUpRight size={16} />
              </Link>
              <Link to="/quote" className="btn-brutal btn-brutal-outline" data-testid="hero-quote-btn">
                Request A Quote
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative min-h-[380px] md:min-h-full border-l-0 md:border-l-2 border-black overflow-hidden">
            <div className="absolute inset-0 grid grid-cols-2">
              {/* Left half: American Flag 250 tee */}
              <div className="relative overflow-hidden border-r-2 border-black">
                <img
                  src="/media/hero.webp"
                  alt="Down To Film Apparel — American flag DTF transfer on a black tee"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: "35% 60%" }}
                />
              </div>
              {/* Right half: Straight Up Hauling decals */}
              <div className="relative overflow-hidden">
                <img
                  src="/media/hero-2.webp"
                  alt="Precision Speed Shop nitrous bottle wrap by The Sticky Chick"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Darken overlay for readability */}
            <div className="absolute inset-0 bg-black/30 pointer-events-none" />
            <div className="grain-overlay" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
              <p className="label-mono text-white/70">WORKSHOP / IN-HOUSE</p>
              <p className="font-display text-3xl md:text-4xl mt-2 leading-none">
                DTF PRINTED.<br />HEAT PRESSED.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PROMISES BENTO */}
      <section className="section-pad">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="label-mono text-[color:var(--muted-text)]">WHY WORK WITH US</p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.9] mt-3">
                THE FOUR THINGS<br />WE NEVER COMPROMISE
              </h2>
            </div>
            <p className="font-body text-[color:var(--muted-text)] max-w-sm">
              Every job runs through the same four rules. It&rsquo;s what keeps our
              customers coming back — from bands to bakeries to boat clubs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {promises.map((p) => (
              <div
                key={p.title}
                className="brutal-card p-6 hard-shadow"
                data-testid={`promise-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-[color:var(--primary)] text-white inline-flex items-center justify-center">
                    <p.icon size={22} />
                  </div>
                  <span className="label-mono text-[color:var(--muted-text)]">{p.tag}</span>
                </div>
                <h3 className="font-display text-3xl mt-6 leading-none">{p.title}</h3>
                <p className="font-body text-[color:var(--muted-text)] mt-3">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL BRAND */}
      <section className="border-y-2 border-black bg-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2">
          <div
            className="p-10 md:p-16 border-b-2 md:border-b-0 md:border-r-2 border-black relative z-20"
            style={{
              backgroundImage:
                "linear-gradient(180deg, var(--dtf-yellow-soft) 0%, transparent 60%)",
            }}
          >
            <p className="label-mono text-dtf-blue">BRAND / 01</p>
            <div className="mt-6 mb-6 h-40 md:h-52 flex items-center justify-center">
              <img
                src="/logos/down-to-film.png"
                alt="Down To Film Apparel"
                className="h-full w-auto object-contain"
                data-testid="brand-logo-dtf"
              />
            </div>
            <h3 className="font-display text-4xl md:text-5xl leading-[0.9] text-dtf-blue">
              DOWN TO FILM<br />APPAREL
            </h3>
            <p className="font-body text-[color:var(--muted-text)] mt-5 max-w-md">
              Comfortable apparel decorated with vibrant direct-to-film transfers
              and heat-pressed by hand. Made for crews, brands, and workshops
              that want their name worn — not scrolled past.
            </p>
            <a
              href="mailto:Downtofilmapparel@gmail.com"
              className="label-mono inline-flex items-center gap-2 mt-6 text-dtf-blue hover:opacity-70 break-all"
              data-testid="brand-email-dtf"
            >
              ✉ Downtofilmapparel@gmail.com
            </a>
            <div className="mt-6">
              <DTFMenu variant="card" data-testid="brand-apparel-menu" />
            </div>
          </div>
          <div
            className="p-10 md:p-16 relative z-10"
            style={{
              backgroundImage:
                "linear-gradient(180deg, var(--sc-pink-soft) 0%, transparent 60%)",
            }}
          >
            <p className="label-mono text-sc-purple">BRAND / 02</p>
            <div className="mt-6 mb-6 h-40 md:h-52 flex items-center justify-center">
              <img
                src="/logos/sticky-chick.webp"
                alt="The Sticky Chick — Stickers & Graphics"
                className="h-full w-auto object-contain"
                data-testid="brand-logo-sc"
              />
            </div>
            <h3 className="font-display text-4xl md:text-5xl leading-[0.9] text-sc-purple">
              THE STICKY<br />CHICK
            </h3>
            <p className="font-body text-[color:var(--muted-text)] mt-5 max-w-md">
              Permanent adhesive vinyl decals, printable vinyl label sheets, and holographic awesomeness
              from the sticker side of the shop. Weatherproof and
              built to survive dishwashers and daily commutes.
            </p>
            <a
              href="mailto:Thestickychickcali@gmail.com"
              className="label-mono inline-flex items-center gap-2 mt-6 text-sc-purple hover:opacity-70 break-all"
              data-testid="brand-email-sc"
            >
              ✉ Thestickychickcali@gmail.com
            </a>
            <div className="mt-6">
              <SCMenu variant="card" data-testid="brand-sc-menu" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      <CustomerGallery />

      {/* CTA */}
      <EtsyRow />

      <section className="bg-black text-white border-y-2 border-black relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="label-mono text-white/60">READY WHEN YOU ARE</p>
          <h2 className="font-display text-6xl md:text-8xl leading-[0.9] mt-4">
            SEND US THE FILE.<br />
            <span className="text-[color:var(--primary)]">WE&rsquo;LL DO THE REST.</span>
          </h2>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/quote" className="btn-brutal btn-brutal-red" data-testid="cta-quote-btn">
              Start A Quote <ArrowUpRight size={16} />
            </Link>
            <Link to="/shop" className="btn-brutal" data-testid="cta-shop-btn">
              Browse Ready-Made
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
